/**
 * i18n Module — Internationalization System
 *
 * Supported locales: ko, en, ja, zh-Hant, es, pt-BR, th, id
 */

import en from './en.js';
import ko from './ko.js';
import ja from './ja.js';
import zhHant from './zh-Hant.js';
import es from './es.js';
import ptBR from './pt-BR.js';
import th from './th.js';
import id from './id.js';
import type { TranslationKey, Translations } from './en.js';

export type Locale = 'ko' | 'en' | 'ja' | 'zh-Hant' | 'es' | 'pt-BR' | 'th' | 'id';

export const SUPPORTED_LOCALES: Locale[] = ['ko', 'en', 'ja', 'zh-Hant', 'es', 'pt-BR', 'th', 'id'];

export const LOCALE_NAMES: Record<Locale, string> = {
  ko: '한국어',
  en: 'English',
  ja: '日本語',
  'zh-Hant': '繁體中文',
  es: 'Espanol',
  'pt-BR': 'Portugues',
  th: 'ภาษาไทย',
  id: 'Indonesia',
};

const TRANSLATIONS: Record<Locale, Translations> = {
  ko,
  en,
  ja,
  'zh-Hant': zhHant,
  es,
  'pt-BR': ptBR,
  th,
  id,
};

const STORAGE_KEY = 'haunted-house-locale';

let currentLocale: Locale = 'en';
let listeners: Array<() => void> = [];

function findLocaleMatch(langCode: string): Locale | null {
  const normalized = langCode.toLowerCase();

  const exact = SUPPORTED_LOCALES.find(
    (loc) => loc.toLowerCase() === normalized,
  );
  if (exact) return exact;

  const base = normalized.split('-')[0];
  const baseMatch = SUPPORTED_LOCALES.find(
    (loc) => loc.toLowerCase().split('-')[0] === base,
  );
  if (baseMatch) return baseMatch;

  return null;
}

function detectLocaleSync(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Locale;
    if (saved && SUPPORTED_LOCALES.includes(saved)) return saved;
  } catch { /* localStorage unavailable */ }

  try {
    const langs = navigator.languages?.length
      ? navigator.languages
      : [navigator.language];
    for (const lang of langs) {
      const match = findLocaleMatch(lang);
      if (match) return match;
    }
  } catch { /* SSR-safe */ }

  return 'en';
}

async function enhanceWithDeviceApi(): Promise<void> {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return;
  } catch { return; }

  try {
    const { Device } = await import('@capacitor/device');
    const { value } = await Device.getLanguageCode();
    if (value) {
      const match = findLocaleMatch(value);
      if (match && match !== currentLocale) {
        currentLocale = match;
        try {
          localStorage.setItem(STORAGE_KEY, match);
          document.documentElement.lang = match;
        } catch { /* */ }
        for (const fn of listeners) fn();
      }
    }
  } catch { /* Not on Capacitor */ }
}

export function initI18n(): Locale {
  currentLocale = detectLocaleSync();
  try { document.documentElement.lang = currentLocale; } catch { /* */ }
  enhanceWithDeviceApi();
  return currentLocale;
}

export function getLocale(): Locale {
  return currentLocale;
}

export function setLocale(locale: Locale): void {
  if (!SUPPORTED_LOCALES.includes(locale)) return;
  currentLocale = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
    document.documentElement.lang = locale;
  } catch { /* */ }
  for (const fn of listeners) fn();
}

export function onLocaleChange(fn: () => void): () => void {
  listeners.push(fn);
  return () => { listeners = listeners.filter((l) => l !== fn); };
}

export function t(key: TranslationKey, params?: Record<string, string | number>): string {
  let text: string =
    TRANSLATIONS[currentLocale]?.[key] ??
    TRANSLATIONS.en[key] ??
    key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    }
  }
  return text;
}

export function getSupportedLocales(): Array<{ code: Locale; name: string }> {
  return SUPPORTED_LOCALES.map((code) => ({ code, name: LOCALE_NAMES[code] }));
}

export function showLanguagePicker(popupContainer?: HTMLElement): void {
  const container = popupContainer ?? document.getElementById('popup-container');
  if (!container) return;

  const overlay = document.createElement('div');
  overlay.className = 'popup-overlay';
  overlay.dataset.popupId = '__lang_picker';
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });

  const box = document.createElement('div');
  box.className = 'popup-box';
  box.style.cssText = 'max-width:320px;padding:20px;';

  const title = document.createElement('div');
  title.className = 'popup-title';
  title.textContent = t('language');
  title.style.cssText = 'margin-bottom:12px;font-size:18px;font-weight:bold;text-align:center;';
  box.appendChild(title);

  const list = document.createElement('div');
  list.style.cssText = 'display:flex;flex-direction:column;gap:8px;';

  for (const { code, name } of getSupportedLocales()) {
    const btn = document.createElement('button');
    btn.className = 'popup-btn';
    btn.style.cssText = `
      padding:12px 16px;border:2px solid ${code === currentLocale ? '#a855f7' : '#444'};
      border-radius:8px;background:${code === currentLocale ? '#2d1b4e' : '#1a1a2e'};
      color:#fff;font-size:16px;cursor:pointer;text-align:left;
    `;
    btn.textContent = `${name}${code === currentLocale ? ' ✓' : ''}`;
    btn.addEventListener('click', () => {
      if (code !== currentLocale) {
        setLocale(code);
        window.location.reload();
      } else {
        overlay.remove();
      }
    });
    list.appendChild(btn);
  }

  box.appendChild(list);
  overlay.appendChild(box);
  container.appendChild(overlay);
}

export type { TranslationKey, Translations };
